/***********************/
/* Angular + external */
import { Component, ViewChild, ElementRef, Injector } from "@angular/core";

// models
import { ICDTextFieldModel } from "../../data-idc/controls/icd-text-field.model";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ICDBase, ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";

@Component({
  selector: "icd-label",
  host: {
    "[class.icd__label__container]": "true",
    ...ICD_BASE_DEFAULT_HOST_RECORD,
  },
  templateUrl: "./html/icd-label.html",
  standalone: false,
})
export class ICDLabelComponent extends ICDBase<ICDTextFieldModel> {
  /**********************************************************************/
  /* DATA */
  public config: any = {
    maxLength: 255,
  };

  public subData: any = {
    text: "", // internal control model
    tooltip: "",
    defaultValue: null,
  };

  /************************************/
  onModelChange: Function = () => {};
  onModelTouched: Function = () => {};

  /************************************/
  /* Element Ref */
  @ViewChild("inputField", { static: true }) inputField: ElementRef;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZE */

  protected initialize(): void {
    super.initialize();
    this.label = "Label Field";
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API  */

  protected initializeICDSpecificModelProperties(): boolean {
    let error: boolean = false;
    if (this.model instanceof ATDLInterfaceControlModel) {
      // INIT VALUE
      if (typeof this.model.initValue === "string") {
        this.setAndUpdateValue(this.model.initValue);
        this.data.defaultValue = this.model.initValue;
      }
    } else {
      error = true;
    }
    return error;
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  protected handleConstraintUpdate(data: any): void {
    this.handleDynResponses(this.processExternalStateFlowDependency(data, new Map()));
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    this.data.value = value;
    this.model.value = this.data.value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */

  public onFocus(event: any): void {
    this.inputFocused = true;
    this.dirty = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.data.errorMsg = "";
  }

  public onBlur(event: any): void {
    this.inputFocused = false;
    this.updateValue();
    this.validate();

    this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  public focusInput() {
    //setTimeout(() =>  this.inputField.nativeElement.focus(), 50);
  }

  /**********************************************************************/
  /* EXTERNAL API */

  public setDefault(): void {
    this.setAndUpdateValue(this.data.defaultValue);
  }

  protected setAndUpdateValue(value: any): void {
    if (value == null) {
      this.set(null);
      this.updateValue();
    } else if (typeof value === "string" || value instanceof ICDTextFieldModel) {
      this.set(value);
      this.updateValue();
    } else {
      logger.out.error(`ERROR: ${this.constructor.name}.setAndUpdateValue(): Invalid value`);
    }
  }

  public updateValue(): void {
    this.data.defaultValue =
      typeof this.data.text === "string" ? this.data.text : this.data.defaultValue;
    this.data.value = this.compose();
    this.writeValue(this.data.value);
  }

  public load(value: ICDTextFieldModel, propagate?: boolean): void {
    if (value instanceof ICDTextFieldModel || value === null) {
      this.set(value);
      this.updateValue();
      this.validate();

      this.dynRespond(ICDNResponseCommand.LOAD_COMPLETE);

      if (typeof propagate === "boolean" && propagate) {
        this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }
    }
  }

  /**
   * The set() method is used in order to assign value to the internal(displayed) control model ('this.data.text') as well as
   * the auxCheckbox when proper type (ICDTextFieldModel) is provided.
   */
  public set(value: ICDTextFieldModel | string): void {
    if (value instanceof ICDTextFieldModel || typeof value === "string" || value == null) {
      this.dirty = true;
      let textValue = value;
      if (value instanceof ICDTextFieldModel) {
        textValue = value.value;
      }

      if (typeof textValue === "string") {
        if (typeof textValue === "string" && textValue.length > 35) {
          this.data.text = textValue.substring(0, 35) + "..";
          this.data.tooltip = textValue;
        } else {
          this.data.text = textValue;
          this.data.tooltip = "";
        }
      } else if (textValue == null) {
        this.data.text = "";
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid value type`);
      }
    }
  }

  /**
   * The compose() method is used in order to generate an outgoing model for the control (in this case ICDTextFieldModel)
   * which will be assigned to the main model 'this.data.value' and propagated to the panel.
   */
  private compose(): ICDTextFieldModel {
    let model = new ICDTextFieldModel();
    let auxModel = new ICDAuxCheckboxModel();
    auxModel.mode = ICDNAuxMode.DISABLED;
    auxModel.enabled = false;
    auxModel.value = false;
    model.nulledByDependency = this.data?.value?.nulledByDependency ?? false;
    model.disabled = this.disabled || this.data.disabled || !this.visible;
    model.auxModel = auxModel;
    model.value = this.data.tooltip !== "" ? this.data.tooltip : this.data.text;
    return model;
  }

  public validateIC(): boolean {
    this.data.state = ICNInputControlState.VALID;
    this.data.errorMsg = "";
    return true;
  }

  public resetIC(): void {
    // Internal model
    this.data.text = "";
    this.data.tooltip = "";
    this.data.defaultValue = null;
    // Label
    this.label = "Label Field";
    // Reset config
    this.config.maxLength = 255;
  }
}
